import {
  type LeadStatusChangeRequestInboxItem,
  type LeadStatusChangeRequestT,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { Check, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { canReviewLeadApprovalRequest } from '../lead-approval-access';
import { formatStatusWithSubStatus } from '../stage-label';
import { useApproveStatusChangeRequest, useRejectStatusChangeRequest } from '../use-leads';

import { DestinationChangeAssigneePicker } from './DestinationChangeAssigneePicker';

import { useCurrentUser } from '@/features/auth/use-auth';
import { LEAD_APPROVAL_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';

export type LeadApprovalReviewAction = 'approve' | 'reject';

/**
 * 2026-08-01. Shared approval review dialog. Was inline on
 * `/leads/approvals`; lifted here so the lead detail header's
 * "Awaiting approval" banner can open the SAME modal instead of
 * bouncing the reviewer to the inbox. Both dialogs (read-only detail
 * + the approve / reject confirmation) live inside one component so a
 * caller only tracks "which request is open".
 *
 * Every review goes through `useApproveStatusChangeRequest` /
 * `useRejectStatusChangeRequest`, which write the fresh lead detail
 * into the cache and invalidate the approvals queries - so acting from
 * lead detail updates the pending-approvals inbox (and its badge /
 * count) and vice versa, with no manual refresh.
 */

export const formatApprovalWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Lead detail carries the bare request (no lead columns - it already
 * knows the lead). Widen it to the inbox row shape the dialog renders.
 */
export const toApprovalInboxItem = (
  request: LeadStatusChangeRequestT,
  lead: { id: string; reference: string; createdAt: string; customer: { displayName: string } },
): LeadStatusChangeRequestInboxItem => ({
  ...request,
  leadId: lead.id,
  leadReference: lead.reference,
  customerName: lead.customer.displayName,
  leadCreatedAt: lead.createdAt,
});

const requestKindLabel = (kind: LeadStatusChangeRequestT['kind']): string => {
  switch (kind) {
    case 'DESTINATION_CHANGE':
      return 'Destination change';
    case 'WON_TRANSITION':
      return 'Win request';
    case 'STATUS_CHANGE':
      return 'Status change';
    default:
      return 'LOST request';
  }
};

interface LeadApprovalReviewDialogProps {
  /** The open request, or null when nothing is open. */
  request: LeadStatusChangeRequestInboxItem | null;
  /**
   * Skip the detail view and open straight on the confirmation step -
   * used by the inbox's per-row Approve / Reject buttons.
   */
  initialAction?: LeadApprovalReviewAction | null;
  onClose: () => void;
  /**
   * Left-hand footer slot of the detail view. The inbox passes an
   * "Open lead" button; lead detail passes a link back to the inbox.
   */
  footerLeft?: React.ReactNode;
}

export const LeadApprovalReviewDialog = ({
  request,
  initialAction = null,
  onClose,
  footerLeft,
}: LeadApprovalReviewDialogProps): React.JSX.Element => {
  const me = useCurrentUser();
  const approve = useApproveStatusChangeRequest();
  const reject = useRejectStatusChangeRequest();
  const submitting = approve.isPending || reject.isPending;

  const [action, setAction] = useState<LeadApprovalReviewAction | null>(initialAction);
  const [reviewerNote, setReviewerNote] = useState('');
  // Destination-change approvals only: the agent to hand the lead to in the
  // same step. Null leaves the owner as they are.
  const [assigneeId, setAssigneeId] = useState<string | null>(null);

  // Re-seed whenever the caller opens a different request (or opens the
  // same one straight on Approve / Reject) so a stale note, step or picked
  // agent from the previous open never carries over.
  useEffect(() => {
    setAction(initialAction);
    setReviewerNote('');
    setAssigneeId(null);
  }, [request?.id, initialAction]);

  const canReview = request ? canReviewLeadApprovalRequest(request, me) : false;
  const isPendingRequest = request?.status === 'PENDING';
  const showReviewActions = Boolean(isPendingRequest && canReview);
  const isDestination = request?.kind === 'DESTINATION_CHANGE';
  // Short subject for the confirmation title ("Approve <subject> for L-…").
  // STATUS_CHANGE used to fall through to "LOST" here, which read as the
  // wrong decision on a plain stage move.
  const confirmSubject = isDestination
    ? 'destination change'
    : request?.kind === 'WON_TRANSITION'
      ? 'WON'
      : request?.kind === 'STATUS_CHANGE'
        ? 'status change'
        : 'LOST';

  const submitReview = async (): Promise<void> => {
    if (!request || !action) return;
    const payload = { reviewerNote: reviewerNote.trim() || null };
    try {
      if (action === 'approve') {
        await approve.mutateAsync({
          id: request.leadId,
          requestId: request.id,
          // The server only accepts a reassignment on a destination change.
          payload:
            isDestination && assigneeId ? { ...payload, assignedAgentId: assigneeId } : payload,
        });
      } else {
        await reject.mutateAsync({ id: request.leadId, requestId: request.id, payload });
      }
      onClose();
    } catch {
      // toast handled by mutation hooks
    }
  };

  return (
    <>
      <UzDialog
        open={request !== null && action === null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={
          request
            ? `${requestKindLabel(request.kind)} - ${request.leadReference}`
            : 'Approval details'
        }
        description={
          request
            ? request.status === 'PENDING'
              ? canReview
                ? 'Review the request context below. Approve / Reject act on this request.'
                : 'Waiting on your team lead or an admin to decide this request.'
              : `This request was ${humanEnum(LEAD_APPROVAL_STATUS_LABEL, request.status).toLowerCase()}.`
            : undefined
        }
        size="lg"
        footer={
          request ? (
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex items-center">{footerLeft}</div>
              <div className="flex gap-2">
                <UzButton variant="ghost" onClick={onClose}>
                  Close
                </UzButton>
                {showReviewActions && (
                  <>
                    <UzButton
                      variant="outline"
                      onClick={() => {
                        setReviewerNote('');
                        setAction('reject');
                      }}
                    >
                      <X className="mr-1 h-4 w-4" />
                      Reject
                    </UzButton>
                    <UzButton
                      onClick={() => {
                        setReviewerNote('');
                        setAction('approve');
                      }}
                    >
                      <Check className="mr-1 h-4 w-4" />
                      Approve
                    </UzButton>
                  </>
                )}
              </div>
            </div>
          ) : null
        }
      >
        {request && <ApprovalDetailContent row={request} />}
      </UzDialog>

      <UzDialog
        open={request !== null && action !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={`${action === 'approve' ? 'Approve' : 'Reject'} ${confirmSubject} for ${
          request?.leadReference ?? 'lead'
        }`}
        description={
          action === 'approve'
            ? isDestination
              ? 'The lead destination will update immediately after you confirm.'
              : 'The lead status will update immediately after you confirm.'
            : isDestination
              ? 'The lead keeps its current destination. The requestor will be notified.'
              : 'The lead stays in its current status. The requestor will be notified.'
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </UzButton>
            <UzButton
              variant={action === 'reject' ? 'outline' : 'default'}
              onClick={() => void submitReview()}
              loading={submitting}
            >
              {action === 'approve' ? 'Confirm approval' : 'Confirm rejection'}
            </UzButton>
          </div>
        }
      >
        {request && (
          <div className="space-y-3 text-sm text-zinc-700">
            {isDestination ? (
              <p>
                Destination: <strong>{request.fromDestination?.name ?? 'None'}</strong> →{' '}
                <strong>{request.toDestination?.name ?? 'Unknown'}</strong>
              </p>
            ) : (
              <p>
                Move:{' '}
                <strong>
                  {formatStatusWithSubStatus(request.fromStatus, request.fromSubStatus)}
                </strong>{' '}
                →{' '}
                <strong>{formatStatusWithSubStatus(request.toStatus, request.toSubStatus)}</strong>
                {request.lostReason ? ` (${request.lostReason.name})` : ''}
              </p>
            )}
            <p>
              Requested by <strong>{request.requestedBy.fullName}</strong>
            </p>
            {/* Approving a destination change is also the moment to hand the
                lead to whoever covers the new destination. Not on a rejection
                (nothing changes) or on any other request kind. */}
            {isDestination && action === 'approve' && (
              <DestinationChangeAssigneePicker
                leadId={request.leadId}
                requestId={request.id}
                destinationName={request.toDestination?.name ?? 'the new destination'}
                value={assigneeId}
                onChange={setAssigneeId}
                disabled={submitting}
              />
            )}
            <UzTextarea
              name="reviewerNote"
              label="Reviewer note (optional)"
              rows={3}
              value={reviewerNote}
              onChange={(e) => setReviewerNote(e.target.value)}
              disabled={submitting}
            />
          </div>
        )}
      </UzDialog>
    </>
  );
};

/**
 * 2026-07-09. Read-only, structured detail block used by the detail
 * dialog. Renders every field the inbox row carries in a two-column
 * definition list.
 */
export const ApprovalDetailContent = ({
  row,
}: {
  row: LeadStatusChangeRequestInboxItem;
}): React.JSX.Element => {
  const statusTone: 'success' | 'destructive' | 'secondary' =
    row.status === 'APPROVED' ? 'success' : row.status === 'REJECTED' ? 'destructive' : 'secondary';
  return (
    <div className="space-y-4 text-sm text-zinc-700">
      <div className="flex flex-wrap items-center gap-2">
        <UzBadge tone={statusTone} pill>
          {humanEnum(LEAD_APPROVAL_STATUS_LABEL, row.status)}
        </UzBadge>
        <UzBadge tone="secondary" pill>
          {requestKindLabel(row.kind)}
        </UzBadge>
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <DetailRow label="Lead reference" value={row.leadReference} />
        <DetailRow label="Lead generated" value={formatApprovalWhen(row.leadCreatedAt)} />
        <DetailRow label="Customer" value={row.customerName ?? 'No customer name'} />
        <DetailRow label="Requested by" value={row.requestedBy.fullName} />
        <DetailRow
          label="Assigned reviewer"
          value={row.assignedReviewer?.fullName ?? 'Admin pool'}
        />
        <DetailRow label="Submitted" value={formatApprovalWhen(row.requestedAt)} />
        {row.reviewedAt && (
          <DetailRow label="Reviewed" value={formatApprovalWhen(row.reviewedAt)} />
        )}
        {row.reviewedBy && <DetailRow label="Reviewed by" value={row.reviewedBy.fullName} />}
      </dl>

      <div className="rounded-md border border-zinc-200 bg-zinc-50/60 px-4 py-3">
        <p className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
          Requested change
        </p>
        {row.kind === 'DESTINATION_CHANGE' ? (
          <p className="mt-1 text-sm text-zinc-800">
            <strong>{row.fromDestination?.name ?? 'None'}</strong>{' '}
            <span className="text-zinc-400">→</span>{' '}
            <strong>{row.toDestination?.name ?? 'Unknown'}</strong>
          </p>
        ) : (
          <p className="mt-1 text-sm text-zinc-800">
            <strong>{formatStatusWithSubStatus(row.fromStatus, row.fromSubStatus)}</strong>{' '}
            <span className="text-zinc-400">→</span>{' '}
            <strong>{formatStatusWithSubStatus(row.toStatus, row.toSubStatus)}</strong>
            {row.lostReason ? (
              <span className="mt-1 block text-xs text-zinc-500">
                Reason: {row.lostReason.name}
              </span>
            ) : null}
          </p>
        )}
      </div>

      {row.requestorNote && (
        <div className="rounded-md border border-zinc-200 bg-white px-4 py-3">
          <p className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
            Requestor note
          </p>
          <p className="mt-1 text-sm whitespace-pre-line text-zinc-800">"{row.requestorNote}"</p>
        </div>
      )}

      {row.reviewerNote && (
        <div className="rounded-md border border-zinc-200 bg-white px-4 py-3">
          <p className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
            Reviewer note
          </p>
          <p className="mt-1 text-sm whitespace-pre-line text-zinc-800">"{row.reviewerNote}"</p>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }): React.JSX.Element => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">{label}</dt>
    <dd className="truncate text-sm text-zinc-800">{value}</dd>
  </div>
);
